import type { NamedSet } from 'zustand/middleware';

import {
	type CookieConsent,
	getStoredConsent,
	updateCookiesBasedOnConsent,
	acceptAllCookies,
	declineAllCookies,
	areCookiesSaved,
} from '../../components/cookies/cookieUtils';
import { cookieNoticePath } from '../../router/routes/cookie';
import type { ZustandStore } from '../store.type';

type CookieCutterState = {
	cookies: CookieConsent & {
		open: boolean;
		showManageCookies: boolean;
	};
};

type CookieCutterActions = {
	setIsCookieCutterOpen: (open: boolean) => void;
	setShowManageCookies: (show: boolean) => void;
	togglePreferences: () => void;
	toggleStatistics: () => void;
	toggleMarketing: () => void;
	acceptAll: () => void;
	declineAll: () => void;
	saveConsent: () => void;
	initializeCookiesFromStorage: () => void;
};

export type CookieCutterSlice = CookieCutterState & CookieCutterActions;

const storedConsent = getStoredConsent();

const cookieCutterInitialState: CookieCutterState = {
	cookies: {
		...storedConsent,
		open: !areCookiesSaved() && globalThis.location.pathname !== cookieNoticePath,
		showManageCookies: false,
	},
};

export function cookieCutterSlice(set: NamedSet<ZustandStore>): CookieCutterSlice {
	return {
		...cookieCutterInitialState,
		initializeCookiesFromStorage(): void {
			set(
				state => {
					const storedConsent = getStoredConsent();
					return {
						...state,
						cookies: {
							...state.cookies,
							preferences: storedConsent.preferences,
							statistics: storedConsent.statistics,
							marketing: storedConsent.marketing,
						},
					};
				},
				false,
				'[initializeCookiesFromStorage]',
			);
		},
		setIsCookieCutterOpen(open: boolean): void {
			set(
				(state: CookieCutterState) => ({
					...state,
					cookies: {
						...state.cookies,
						open,
					},
				}),
				false,
				'[setIsCookieCutterOpen]',
			);
		},
		setShowManageCookies(show: boolean): void {
			set(
				(state: CookieCutterState) => ({
					...state,
					cookies: {
						...state.cookies,
						showManageCookies: show,
					},
				}),
				false,
				'[setShowManageCookies]',
			);
		},
		togglePreferences(): void {
			set(
				(state: CookieCutterState) => ({
					...state,
					cookies: {
						...state.cookies,
						preferences: !state.cookies.preferences,
					},
				}),
				false,
				'[togglePreferences]',
			);
		},
		toggleStatistics(): void {
			set(
				(state: CookieCutterState) => ({
					...state,
					cookies: {
						...state.cookies,
						statistics: !state.cookies.statistics,
					},
				}),
				false,
				'[toggleStatistics]',
			);
		},
		toggleMarketing(): void {
			set(
				(state: CookieCutterState) => ({
					...state,
					cookies: {
						...state.cookies,
						marketing: !state.cookies.marketing,
					},
				}),
				false,
				'[toggleMarketing]',
			);
		},
		acceptAll(): void {
			set(
				(state: CookieCutterState) => {
					acceptAllCookies();
					return {
						...state,
						cookies: {
							...state.cookies,
							preferences: true,
							statistics: true,
							marketing: true,
						},
					};
				},
				false,
				'[acceptAll]',
			);
		},
		declineAll(): void {
			set(
				(state: CookieCutterState) => {
					declineAllCookies();
					return {
						...state,
						cookies: {
							...state.cookies,
							preferences: false,
							statistics: false,
							marketing: false,
						},
					};
				},
				false,
				'[declineAll]',
			);
		},
		saveConsent(): void {
			set(
				(state: CookieCutterState) => {
					const consent: CookieConsent = {
						necessary: true,
						preferences: state.cookies.preferences,
						statistics: state.cookies.statistics,
						marketing: state.cookies.marketing,
					};
					updateCookiesBasedOnConsent(consent);
					return {
						...state,
						cookies: {
							...state.cookies,
							showManageCookies: false,
							open: false,
						},
					};
				},
				false,
				'[saveConsent]',
			);
		},
	};
}
